import { useState, useEffect } from 'react';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

interface BudgetCategory {
  name: string;
  usAmount: number;
  ieAmount: number;
  icon: string;
}

export default function BudgetPlannerPage() {
  const currentYear = new Date().getFullYear();
  const [location, setLocation] = useState<'dublin' | 'cork' | 'galway' | 'other'>('dublin');
  const [familySize, setFamilySize] = useState(1);
  
  useEffect(() => {
    document.title = 'Ireland Budget Planner - Compare USA vs Ireland Living Costs | Ireland Bound';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Interactive budget planner to compare your USA expenses with Ireland costs. Customize by city, family size, and lifestyle to plan your relocation budget.');
    }
  }, []);
  
  const [categories, setCategories] = useState<BudgetCategory[]>([
    { name: 'Rent (1-bed apartment)', usAmount: 1500, ieAmount: 1800, icon: 'ri-home-4-line' },
    { name: 'Utilities (electricity, gas, water)', usAmount: 150, ieAmount: 180, icon: 'ri-flashlight-line' },
    { name: 'Internet & Phone', usAmount: 80, ieAmount: 60, icon: 'ri-wifi-line' },
    { name: 'Groceries', usAmount: 400, ieAmount: 450, icon: 'ri-shopping-cart-line' },
    { name: 'Transportation', usAmount: 200, ieAmount: 120, icon: 'ri-bus-line' },
    { name: 'Health Insurance', usAmount: 450, ieAmount: 150, icon: 'ri-heart-pulse-line' },
    { name: 'Dining Out', usAmount: 300, ieAmount: 250, icon: 'ri-restaurant-line' },
    { name: 'Entertainment', usAmount: 200, ieAmount: 150, icon: 'ri-movie-line' },
    { name: 'Gym/Fitness', usAmount: 50, ieAmount: 45, icon: 'ri-run-line' },
    { name: 'Clothing', usAmount: 150, ieAmount: 120, icon: 'ri-shirt-line' },
  ]);

  const locationMultipliers = {
    dublin: 1.0,
    cork: 0.85,
    galway: 0.88,
    other: 0.75
  };

  const adjustedCategories = categories.map(cat => ({
    ...cat,
    ieAmount: Math.round(cat.ieAmount * locationMultipliers[location] * familySize)
  }));

  const totalUS = categories.reduce((sum, cat) => sum + cat.usAmount, 0) * familySize;
  const totalIE = adjustedCategories.reduce((sum, cat) => sum + cat.ieAmount, 0);
  const difference = totalIE - totalUS;
  const percentDiff = ((difference / totalUS) * 100).toFixed(1);

  const updateCategory = (index: number, field: 'usAmount' | 'ieAmount', value: number) => {
    const newCategories = [...categories];
    newCategories[index][field] = value;
    setCategories(newCategories);
  };

  const addCategory = () => {
    setCategories([...categories, { 
      name: 'New Category', 
      usAmount: 0, 
      ieAmount: 0, 
      icon: 'ri-add-circle-line' 
    }]);
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
        <Navbar />

        <div className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <i className="ri-calculator-line"></i>
                Interactive Budget Tool
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-4">
                Ireland Budget Planner
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Compare your USA expenses with Ireland costs. Customize by location, family size, and lifestyle to create your personalized relocation budget.
              </p>
            </div>

            {/* Settings */}
            <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Situation</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Ireland Location
                  </label>
                  <select
                    value={location}
                    onChange={(e) => setLocation(e.target.value as any)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    <option value="dublin">Dublin (Most Expensive)</option>
                    <option value="galway">Galway</option>
                    <option value="cork">Cork</option>
                    <option value="other">Other Cities (Most Affordable)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Family Size: {familySize} {familySize === 1 ? 'person' : 'people'}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="6"
                    value={familySize}
                    onChange={(e) => setFamilySize(Number(e.target.value))}
                    className="w-full h-2 bg-emerald-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1</span>
                    <span>6</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Budget Comparison */}
            <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Monthly Budget Breakdown</h2>
                <button
                  onClick={addCategory}
                  className="bg-emerald-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-emerald-700 transition-colors whitespace-nowrap text-sm flex items-center gap-2"
                >
                  <i className="ri-add-line"></i>
                  Add Category
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Category</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">USA ($)</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Ireland (€)</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Difference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adjustedCategories.map((cat, index) => {
                      const diff = cat.ieAmount - (cat.usAmount * familySize);
                      const isHigher = diff > 0;
                      return (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-3">
                              <i className={`${cat.icon} text-xl text-emerald-600`}></i>
                              <input
                                type="text"
                                value={cat.name}
                                onChange={(e) => {
                                  const newCategories = [...categories];
                                  newCategories[index].name = e.target.value;
                                  setCategories(newCategories);
                                }}
                                className="font-medium text-gray-900 border-none focus:ring-0 bg-transparent"
                              />
                            </div>
                          </td>
                          <td className="py-4 px-4 text-right">
                            <input
                              type="number"
                              value={cat.usAmount * familySize}
                              onChange={(e) => updateCategory(index, 'usAmount', Number(e.target.value) / familySize)}
                              className="w-24 text-right border border-gray-300 rounded px-2 py-1 text-sm"
                            />
                          </td>
                          <td className="py-4 px-4 text-right font-semibold text-gray-900">
                            €{cat.ieAmount.toLocaleString()}
                          </td>
                          <td className="py-4 px-4 text-right">
                            <span className={`font-semibold ${isHigher ? 'text-red-600' : 'text-emerald-600'}`}>
                              {isHigher ? '+' : ''}{diff > 0 ? '€' : '-€'}{Math.abs(diff).toLocaleString()}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-300 bg-gray-50">
                      <td className="py-4 px-4 font-bold text-gray-900">TOTAL MONTHLY</td>
                      <td className="py-4 px-4 text-right font-bold text-gray-900">
                        ${totalUS.toLocaleString()}
                      </td>
                      <td className="py-4 px-4 text-right font-bold text-gray-900">
                        €{totalIE.toLocaleString()}
                      </td>
                      <td className="py-4 px-4 text-right">
                        <span className={`font-bold text-lg ${difference > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                          {difference > 0 ? '+' : ''}{percentDiff}%
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
                <div className="flex items-center gap-3 mb-2">
                  <i className="ri-map-pin-line text-3xl"></i>
                  <h3 className="text-lg font-semibold">USA Monthly</h3>
                </div>
                <p className="text-4xl font-bold">${totalUS.toLocaleString()}</p>
                <p className="text-blue-100 text-sm mt-2">Current expenses</p>
              </div>

              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-6 text-white">
                <div className="flex items-center gap-3 mb-2">
                  <i className="ri-ireland-line text-3xl"></i>
                  <h3 className="text-lg font-semibold">Ireland Monthly</h3>
                </div>
                <p className="text-4xl font-bold">€{totalIE.toLocaleString()}</p>
                <p className="text-emerald-100 text-sm mt-2">Estimated in {location}</p>
              </div>

              <div className={`bg-gradient-to-br ${difference > 0 ? 'from-red-500 to-red-600' : 'from-green-500 to-green-600'} rounded-xl p-6 text-white`}>
                <div className="flex items-center gap-3 mb-2">
                  <i className={`${difference > 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} text-3xl`}></i>
                  <h3 className="text-lg font-semibold">Difference</h3>
                </div>
                <p className="text-4xl font-bold">{difference > 0 ? '+' : ''}€{Math.abs(difference).toLocaleString()}</p>
                <p className="text-white/90 text-sm mt-2">{difference > 0 ? 'More expensive' : 'Less expensive'} ({percentDiff}%)</p>
              </div>
            </div>

            {/* Insights */}
            <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-xl p-8 text-white">
              <h2 className="text-2xl font-bold mb-4">💡 Budget Insights</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold mb-2">Key Differences:</h3>
                  <ul className="space-y-2 text-emerald-50">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line mt-1"></i>
                      <span>Healthcare costs are significantly lower in Ireland</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line mt-1"></i>
                      <span>Public transportation is excellent and affordable</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line mt-1"></i>
                      <span>Housing costs vary greatly by location</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Money-Saving Tips:</h3>
                  <ul className="space-y-2 text-emerald-50">
                    <li className="flex items-start gap-2">
                      <i className="ri-lightbulb-line mt-1"></i>
                      <span>Consider cities outside Dublin for 25% lower costs</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-lightbulb-line mt-1"></i>
                      <span>Use Wise or Revolut for currency transfers</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-lightbulb-line mt-1"></i>
                      <span>Shop at Aldi and Lidl for groceries</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}
